import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';

import { TEntityGridConstraints } from 'app/common/entity-grid/core';
import * as EntityGrid from 'app/common/entity-grid/features';
import { RangeDateFilterModel } from 'app/common/ui/data-grid/filters/range-date/core';
import { HttpRequest } from 'app/libs/http-client';
import { useUserRoles } from 'app/railways/domains/access/features/roles/use-user-roles';

import { TTripConstraints } from '../../core/trip-constraints';
import { getTripsRequest } from '../../requests';
import { cols } from './grid-cols';
import { END_TIME_FILTER_NAME, START_TIME_FILTER_NAME } from './local-params';
import { ManageTrips } from './manage-trips/manage-trips';

export const TripsEntityGrid = observer(() => {
  const userRoles = useUserRoles();

  const mapRequestParams = (constraints?: TTripConstraints): HttpRequest => {
    const filters = { ...constraints?.filter };

    // map data grid RangeDateFilter Value to Server API Filter
    if (START_TIME_FILTER_NAME in filters) {
      const rangeFilter = filters[
        START_TIME_FILTER_NAME
      ] as RangeDateFilterModel['value'];

      filters['lowStartTime'] = rangeFilter.start.toJSON();
      filters['highStartTime'] = rangeFilter.end.toJSON();

      delete filters[START_TIME_FILTER_NAME];
    }

    if (END_TIME_FILTER_NAME in filters) {
      const rangeFilter = filters[
        END_TIME_FILTER_NAME
      ] as RangeDateFilterModel['value'];
      filters['lowFinishTime'] = rangeFilter.start.toJSON();
      filters['highFinishTime'] = rangeFilter.end.toJSON();

      delete filters[END_TIME_FILTER_NAME];
    }

    const updatedConstraints: TTripConstraints = {
      ...constraints,
      sorting: constraints?.sorting || [],
      filter: filters,
    };

    return getTripsRequest(updatedConstraints);
  };

  const initialConstraints = useMemo<TEntityGridConstraints>(
    () => ({
      sorting: [{ name: 'id', direction: 'descend' }],
      filter: {
        [START_TIME_FILTER_NAME]: {
          start: moment().startOf('day').toDate(),
          // 7 day * 24h * 60min * 60sec * 1000ms
          end: moment().add(1, 'week').endOf('day').toDate(),
        },
      },
    }),
    [],
  );

  return (
    <EntityGrid.Root
      columns={cols}
      rowModelType="largeList"
      recordsLimit={1000}
      initialState={initialConstraints}
      request={mapRequestParams}
      userRoles={userRoles}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          padding: 16,
          height: '100%',
        }}
      >
        <EntityGrid.NotificationsRender>
          {(notifications) => <div>{notifications}</div>}
        </EntityGrid.NotificationsRender>

        <div style={{ flexGrow: 1 }}>
          <EntityGrid.Table
            rowSelection="multiple"
            actions={<ManageTrips />}
            persistSettingsId="TripEntityGrid"
          />
        </div>
      </div>
    </EntityGrid.Root>
  );
});
